import {
  IonButton,
  IonPage,
  IonText,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonFooter,
  IonAlert,
  IonContent,
  IonIcon,
  useIonRouter,
} from '@ionic/react';
import { lockClosed, mailOutline, shield } from 'ionicons/icons';
import RCField, { FormProvider, useForm } from 'rc-field-form';
import { FormFinishInfo } from 'rc-field-form/es/FormContext';
import React, { useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import BackButton from '@components/BackButton';
import Content from '@components/Content';
import Field from '@components/Form/Field';
import InputField from '@components/Form/InputField';
import Product from '@custom-types/Product';
import RoutePath from '@custom-types/RoutePath';
import useLoading from '@hooks/useLoading';
import { LoginType } from '@hooks/useLogin';
import { generateRoute, getProduct } from '@services/navigation-service';

import { sendForgotPasswordEmail } from './service';

const Container = styled(IonPage)`
  // reduce overlapping when navigating to and from this screen
  background-color: var(--ion-background-color, #fff);
`;

const IconContainer = styled.div`
  position: relative;
  margin-top: 40px;
`;

const ShieldIcon = styled(IonIcon)`
  color: #ff9e1b;
  width: 138px;
  height: 138px;
`;

const LockIcon = styled(IonIcon)`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 15px;
  margin: auto;
  color: white;
  width: 62px;
  height: 62px;
`;

const MailIcon = styled(IonIcon)`
  margin-top: 40px;
  color: #ff9e1b;
  width: 138px;
  height: 138px;
`;

const Title = styled(IonText)`
  margin-bottom: 20px;
`;

const Toolbar = styled(IonToolbar)`
  --background: transparent;
`;

const Button = styled(IonButton)`
  padding: 0 16px;
  margin-bottom: 16px;
`;

const Footer = styled(IonFooter)`
  padding-bottom: 48px;
`;

const Form = styled(RCField)`
  width: 100%;
  padding-top: 32px;
`;

const ForgetAccessCode = () => {
  const product = getProduct() === Product.LeadCapture ? 'Lead Capture' : 'Onsite';

  return (
    <Container>
      <IonHeader className="ion-no-border">
        <Toolbar>
          <IonButtons slot="start">
            <BackButton />
          </IonButtons>
        </Toolbar>
      </IonHeader>
      <IonContent>
        <Content>
          <MailIcon icon={mailOutline} />
          <Title>
            <h1>{t`Forgot Access Code?`}</h1>
          </Title>
          <IonText className="ion-text-center" color="dark">
            {t`Your Access Code can be found in the ${product} App invitation email sent to your inbox.`}
          </IonText>
          <br />
          <IonText className="ion-text-center" color="dark">
            {t`If you cannot locate the email, please contact the Event Organizer.`}
          </IonText>
        </Content>
      </IonContent>
      <Footer className="ion-no-border">
        <Button
          expand="block"
          role="button"
          fill="outline"
          routerLink={generateRoute(RoutePath.LoginWithCode)}
        >
          {t`Back`}
        </Button>
      </Footer>
    </Container>
  );
};

interface ForgotPasswordProps extends Partial<RouteComponentProps<{ email?: string }>> {
  type: LoginType;
}

const ForgotPassword = ({ type, match }: ForgotPasswordProps) => {
  const { push: pushRoute } = useIonRouter();
  const product = getProduct();
  const [form] = useForm();
  const { presentLoading, dismissLoading } = useLoading();
  const [submitting, setSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const initialEmailValue = match?.params.email;

  const onFinish = async (_: string, { forms }: FormFinishInfo) => {
    setSubmitting(true);
    try {
      await presentLoading();
      const allValues = await Promise.all(
        Object.values(forms).map(f => f.validateFields().then(values => values))
      );

      const { email } = allValues.reduce((acc, value) => ({ ...acc, ...value }), {});

      await sendForgotPasswordEmail({ email });
      pushRoute(generateRoute(RoutePath.ForgotPasswordSuccess, { email }));
    } catch (err: any) {
      if (!err?.errorFields) {
        setShowAlert(true);
      }
    } finally {
      await dismissLoading();
      setSubmitting(false);
    }
  };

  if (type === LoginType.WithAccessCode) {
    return <ForgetAccessCode />;
  }

  // This is used for linking between the login and forgot password pages. We need to default it to
  // undefined so that the router doesn't complain about an empty param or navigate to /:email
  const email = form.getFieldValue('email') || undefined;

  return (
    <Container>
      <FormProvider
        // skipcq: JS-0417
        onFormFinish={onFinish}
      >
        <IonHeader className="ion-no-border">
          <Toolbar>
            <IonButtons slot="start">
              <BackButton />
            </IonButtons>
          </Toolbar>
        </IonHeader>
        <Content>
          <IconContainer>
            <ShieldIcon icon={shield} />
            <LockIcon icon={lockClosed} />
          </IconContainer>
          <Title>
            <h1>{t`Forgot Password?`}</h1>
          </Title>
          <IonText className="ion-text-center" color="dark">
            {t`To reset your password, enter the email address associated with your EventMobi account. You'll receive a link to reset your password.`}
          </IonText>
          <Form form={form} initialValues={{ email: initialEmailValue }} name="email-form">
            <Field
              name="email"
              rules={[
                { required: true, message: t`Email Address is required.` },
                { type: 'email', message: t`Invalid email address.` },
              ]}
            >
              <InputField
                inputMode="email"
                role="textbox"
                aria-label="email"
                data-testid="email"
                label={t`Email Address`}
              />
            </Field>
          </Form>
        </Content>
        <Footer className="ion-no-border">
          <Form>
            <Button
              expand="block"
              type="submit"
              role="button"
              disabled={submitting}
            >{t`Send Email`}</Button>
            <Button
              expand="block"
              role="button"
              fill="outline"
              routerLink={
                product === Product.LeadCapture
                  ? generateRoute(RoutePath.Login)
                  : generateRoute(RoutePath.LoginWithPassword, { email })
              }
            >
              {t`Back To Login`}
            </Button>
          </Form>
        </Footer>
      </FormProvider>
      <IonAlert
        isOpen={showAlert}
        // skipcq: JS-0417
        onDidDismiss={() => setShowAlert(false)}
        header={t`Error`}
        message={t`We were unable to process your request at this time. Please try again`}
        buttons={[t`OK`]}
      />
    </Container>
  );
};

export default ForgotPassword;
